import ActionsDropdown from "@/components/ActionsDropdown/ActionsDropdown";
import FakeLink from "@/components/FakeLink/FakeLink";
import ImpactPill from "@/components/Impact/ImpactPill";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import Table, { Column } from "@/components/Table/Table";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import { useTableSelection } from "@/hooks/useTableSelection";
import useTranslation from "@/hooks/useTranslation";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import DeleteTravelModeAction from "@/pages/employee/travel_modes/DeleteTravelModeAction";
import DeleteTravelModeReferenceAction from "@/pages/employee/travel_modes/DeleteTravelModeReferenceAction";
import TravelReferenceSelector from "@/pages/employee/travel_modes/TravelReferenceSelector";
import {
  TravelMode,
  useGetTravelModeOverview,
  useTravelModeStatusPerYear,
} from "@/pages/employee/travel_modes/useTravelMode";
import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Col, Dropdown, DropdownProps, Row, Space, Tooltip } from "antd";
import React from "react";
import styles from "./TravelModes.module.scss";

const columns: Column<TravelMode>[] = [
  {
    key: "name",
    label: "Travel mode",
    minWidth: "182px",
    searchable: true,
    sortable: true,
  },
  {
    key: "impact_reference",
    label: "Impact reference",
    minWidth: "168px",
    sortable: true,
  },
  {
    key: "footprint",
    label: "Footprint",
    minWidth: "300px",
    renderer: (record) => (
      <ImpactPill
        impact={record.impact}
        type="borderless"
        before={
          record.unit && <span style={{ fontSize: "12px", color: "#8C8C8C" }}>1 {record.unit}</span>
        }
      />
    ),
  },
  {
    key: "relations",
    label: "Relations",
    minWidth: "140px",
    sortable: true,
  },
  {
    key: "status",
    label: "Status",
    minWidth: "170px",
    sortable: true,
    filterable: true,
    renderer: (record) => (
      <StatusTag
        status={record.status}
        showIcon={false}
      />
    ),
  },
  {
    key: "actions",
    label: "Actions",
    minWidth: "80px",
    maxWidth: "80px",
    noLink: true,
    renderer: (record) => (
      <ActionsDropdown
        items={[
          {
            label: (
              <TravelReferenceSelector selectedItems={[record]}>
                <span>Edit impact reference</span>
              </TravelReferenceSelector>
            ),
            icon: <EditOutlined />,
            key: "0",
            closeOnClick: true,
            fakeLink: true,
          },
          {
            label: (
              <DeleteTravelModeReferenceAction selectedTravelModes={[record]}>
                <span>Delete impact reference</span>
              </DeleteTravelModeReferenceAction>
            ),
            icon: <DeleteOutlined />,
            danger: true,
            key: "1",
          },
          {
            label: (
              <DeleteTravelModeAction travelModes={[record]}>
                <span>Delete travel mode</span>
              </DeleteTravelModeAction>
            ),
            icon: <DeleteOutlined />,
            danger: true,
            key: "1",
            disabled: record.relations > 0,
          },
        ]}
      />
    ),
    align: "end",
  },
];

function TravelModes() {
  const translation = useTranslation();
  const [moreDropdownOpen, setMoreDropdownOpen] = React.useState(false);

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "external_id", order: "ascend" },
    });

  const { year } = useDashboardYear();
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const { data, isLoading, isFetching } = useGetTravelModeOverview({
    filter,
    sort,
    search,
    pagination,
    year,
    showUnrelated,
  });

  const { data: yearsData, isLoading: yearsIsLoading } = useTravelModeStatusPerYear();

  const { selectedItems, selectedKeys, setSelectedItems, onSelectedKeysChange } =
    useTableSelection<TravelMode>({
      data: data?.data,
    });

  const handleOpenChange: DropdownProps["onOpenChange"] = (nextOpen, info) => {
    if (info.source === "trigger" || nextOpen) {
      setMoreDropdownOpen(nextOpen);
    }
  };

  const deselectUndeletableTravelModes = () => {
    setSelectedItems((item) => [...item.filter((x) => x.relations === 0)]);
  };

  const resetTable = () => {
    setPage(1);
    setSelectedItems([]);
  };

  return (
    <>
      <PageHeader
        title={translation.employee.travel_modes.overview.pageTitle}
        description={translation.employee.travel_modes.overview.pageDescription}
      />
      <Row>
        <Col span={24}>
          <TableHeader
            left={
              selectedItems.length > 0 ? (
                <>
                  <span className={styles["num-selected"]}>{selectedItems.length} selected:</span>
                  <PageButtonGroup loading={isLoading || isFetching}>
                    <TravelReferenceSelector
                      selectedItems={selectedItems}
                      onSuccess={() => onSelectedKeysChange([])}
                    />
                    <DeleteTravelModeAction
                      travelModes={selectedItems}
                      deselectUnDeletableTravelModes={deselectUndeletableTravelModes}
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        disabled={
                          selectedItems.length === 0 || selectedItems.some((x) => x.relations > 0)
                        }
                        ghost
                        danger
                        style={{ zIndex: 20 }}
                      >
                        Delete
                      </Button>
                    </DeleteTravelModeAction>
                    <Dropdown
                      trigger={["click"]}
                      onOpenChange={handleOpenChange}
                      open={moreDropdownOpen}
                      menu={{
                        items: [
                          {
                            label: (
                              <DeleteTravelModeReferenceAction
                                selectedTravelModes={selectedItems}
                                onSuccess={() => setMoreDropdownOpen(false)}
                              >
                                <Space>
                                  <DeleteOutlined />
                                  <span>
                                    {translation.employee.travel_modes.overview.deleteReferenceText}
                                  </span>
                                </Space>
                              </DeleteTravelModeReferenceAction>
                            ),
                            key: "1",
                            danger: true,
                            disabled: selectedItems.length === 0,
                          },
                        ],
                      }}
                    >
                      <Button icon={<MoreOutlined />} />
                    </Dropdown>
                  </PageButtonGroup>
                  {selectedItems.length > 0 && (
                    <FakeLink onClick={() => setSelectedItems([])}>Clear selection</FakeLink>
                  )}
                </>
              ) : (
                <YearSelector
                  disableYears={showUnrelated}
                  optionData={yearsData}
                  isLoading={yearsIsLoading}
                  onChange={resetTable}
                  right={
                    <Tooltip
                      placement="bottom"
                      title="Show travel modes that are not related to a year via commute or business travel"
                    >
                      <>
                        <SwitchWithLabel
                          togglePosition="right"
                          size="small"
                          checked={showUnrelated}
                          onChange={(disabled) => {
                            resetTable();
                            setShowUnrelated(disabled);
                          }}
                        >
                          Year unrelated
                        </SwitchWithLabel>
                      </>
                    </Tooltip>
                  }
                />
              )
            }
            right={
              <>
                <StatusTags
                  data={data}
                  loading={isLoading}
                />
              </>
            }
          />
        </Col>
        <Col span={24}>
          <Table
            loading={isLoading || isFetching}
            checkable={true}
            availableFilters={{
              status: [
                { label: "Complete", value: "complete" },
                { label: "Incomplete", value: "incomplete" },
              ],
            }}
            selectedKeys={selectedKeys}
            onSelectedKeysChange={onSelectedKeysChange}
            rowKey="id"
            data={data?.data || undefined}
            onSearchFilterSortPaginate={onSearchFilterSortPaginate}
            totalRows={data?.total_count || 0}
            pagination={pagination}
            sort={sort}
            search={search}
            filter={filter}
            columns={columns}
            rowLink={(record) => `/employees/travel_modes/${record.id}`}
          />
        </Col>
      </Row>
    </>
  );
}

export default TravelModes;
